import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Empty,
  message,
  Space,
  Tag,
  Typography,
  Tooltip,
  Pagination,
} from 'antd';
import {
  PhoneOutlined,
  MailOutlined,
  MessageOutlined,
  UserOutlined,
  PlusOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import api from '../../services/api';
import LogCommunicationModal from './LogCommunicationModal';

dayjs.extend(relativeTime);

interface CommunicationEntry {
  id: number;
  customer_id: number | null;
  direction: string;
  channel: string;
  source: string | null;
  phone_number: string | null;
  email_address: string | null;
  subject: string | null;
  body: string | null;
  duration_seconds: number | null;
  staff_user_id: number | null;
  notes: string | null;
  staff_name: string | null;
  logged_at: string;
  created_at: string;
}

interface CustomerCommunicationTabProps {
  customerId: number;
  customerPhone?: string;
  customerEmail?: string;
}

const CustomerCommunicationTab: React.FC<CustomerCommunicationTabProps> = ({
  customerId,
  customerPhone,
  customerEmail,
}) => {
  const [entries, setEntries] = useState<CommunicationEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(20);

  const fetchEntries = React.useCallback(async (pageNum: number = 1) => {
    try {
      setLoading(true);
      const response = await api.get(
        `/communications/customer/${customerId}`,
        { params: { page: pageNum, limit } }
      );
      setEntries(response.data.entries || []);
      setTotal(response.data.total || 0);
      setPage(pageNum);
    } catch (error) {
      console.error('Error fetching communications:', error);
      message.error('Failed to load communication history');
    } finally {
      setLoading(false);
    }
  }, [customerId, limit]);

  useEffect(() => {
    fetchEntries(1);
  }, [fetchEntries]);

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'phone':
        return <PhoneOutlined />;
      case 'sms':
        return <MessageOutlined />;
      case 'email':
        return <MailOutlined />;
      case 'in_person':
        return <UserOutlined />;
      default:
        return null;
    }
  };

  const getChannelColor = (channel: string) => {
    switch (channel) {
      case 'phone':
        return 'blue';
      case 'sms':
        return 'green';
      case 'email':
        return 'orange';
      case 'in_person':
        return 'purple';
      default:
        return 'default';
    }
  };

  const getDirectionTag = (direction: string) => {
    const color = direction === 'inbound' ? 'blue' : 'green';
    const label = direction === 'inbound' ? 'Inbound' : 'Outbound';
    return <Tag color={color}>{label}</Tag>;
  };

  const columns = [
    {
      title: 'Date/Time',
      dataIndex: 'logged_at',
      key: 'logged_at',
      width: 150,
      render: (text: string) => (
        <Tooltip title={dayjs(text).format('YYYY-MM-DD HH:mm:ss')}>
          <span>{dayjs(text).fromNow()}</span>
        </Tooltip>
      ),
      sorter: (a: CommunicationEntry, b: CommunicationEntry) =>
        dayjs(b.logged_at).unix() - dayjs(a.logged_at).unix(),
    },
    {
      title: 'Direction',
      dataIndex: 'direction',
      key: 'direction',
      width: 100,
      render: (direction: string) => getDirectionTag(direction),
    },
    {
      title: 'Channel',
      dataIndex: 'channel',
      key: 'channel',
      width: 120,
      render: (channel: string) => (
        <Space size="small">
          {getChannelIcon(channel)}
          <Tag color={getChannelColor(channel)}>
            {channel.charAt(0).toUpperCase() + channel.slice(1)}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Subject/Summary',
      key: 'subject',
      width: 250,
      render: (_: any, record: CommunicationEntry) => {
        const text = record.subject || record.body || '(No content)';
        const truncated = text.length > 50 ? text.substring(0, 50) + '...' : text;
        return <Tooltip title={text}><span>{truncated}</span></Tooltip>;
      },
    },
    {
      title: 'Contact',
      key: 'contact',
      width: 150,
      render: (_: any, record: CommunicationEntry) => {
        if (record.email_address) {
          return (
            <Tooltip title={record.email_address}>
              <span>{record.email_address}</span>
            </Tooltip>
          );
        }
        if (record.phone_number) {
          return <span>{record.phone_number}</span>;
        }
        return '-';
      },
    },
    {
      title: 'Staff',
      dataIndex: 'staff_name',
      key: 'staff_name',
      width: 120,
      render: (text: string | null) => text || '-',
    },
  ];

  if (entries.length === 0 && !loading) {
    return (
      <div style={{ padding: '40px 0' }}>
        <Empty
          description="No communications logged yet"
          style={{ marginBottom: 24 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
        >
          Log First Communication
        </Button>
        <LogCommunicationModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={() => fetchEntries(1)}
          customerId={customerId}
          customerPhone={customerPhone}
          customerEmail={customerEmail}
        />
      </div>
    );
  }

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setModalOpen(true)}
        >
          Log Communication
        </Button>
        <Button
          icon={<ReloadOutlined />}
          onClick={() => fetchEntries(page)}
          loading={loading}
        >
          Refresh
        </Button>
      </Space>

      <Table
        columns={columns}
        dataSource={entries}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
      />

      {total > 0 && (
        <Pagination
          current={page}
          pageSize={limit}
          total={total}
          onChange={fetchEntries}
          style={{ marginTop: 16, textAlign: 'right' }}
        />
      )}

      <LogCommunicationModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSuccess={() => fetchEntries(1)}
        customerId={customerId}
        customerPhone={customerPhone}
        customerEmail={customerEmail}
      />
    </div>
  );
};

export default CustomerCommunicationTab;
